import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { FolderPicker } from '@/components/storage/FolderPicker';
import { DatasetPreviewDialog } from '@/components/storage/PreviewDialog';
import { formatBytes, formatNumber, formatRelative } from '@/lib/format';

const BarisInfo = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-[var(--text-muted)]">{label}</dt>
      <dd className="text-right text-[var(--text-secondary)]">{value}</dd>
    </div>
  ) : null;

/**
 * Rincian satu dataset publik, plus impor ke Penyimpanan Pribadi.
 *
 * Folder tujuan dipilih DI SINI, sebelum tombol impor ditekan - bukan
 * dipindah-pindahkan setelahnya. Impor tidak menggandakan berkas di disk;
 * hanya catatan baru yang menunjuk objek yang sama.
 */
export const DatasetDetailDialog = ({ dataset, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [folderId, setFolderId] = useState('');
  const [inspeksi, setInspeksi] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dataset-detail', dataset?.slug],
    queryFn: () => api.get(`/datasets/${dataset.slug}`),
    enabled: Boolean(dataset),
  });

  const detail = data?.data;

  const impor = useMutation({
    mutationFn: () => api.post(`/datasets/${detail.id}/import`, { folderId: folderId || null }),
    onSuccess: () => {
      toast.success('Dataset diimpor', {
        description: 'Berkasnya kini ada di Penyimpanan Pribadi Anda.',
      });
      queryClient.invalidateQueries({ queryKey: ['storage'] });
      onClose();
    },
    onError: (err) =>
      toast.error('Gagal mengimpor', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (!dataset) return null;

  const contoh = detail?.sampleRows ?? [];
  const headerContoh = contoh.length
    ? [...new Set(contoh.flatMap((r) => Object.keys(r)))].slice(0, 8)
    : [];

  return (
    <Modal
      open
      onClose={onClose}
      title={dataset.title}
      description={dataset.sourceDomain}
      size="lg"
      footer={
        detail?.advanced?.allowDownload !== false && (
          <div className="flex w-full items-end justify-between gap-3">
            <div className="w-64">
              <Field label="Impor ke folder">
                {(props) => <FolderPicker {...props} value={folderId} onChange={setFolderId} />}
              </Field>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" icon={Eye} onClick={() => setInspeksi(true)}>
                Inspek Data
              </Button>
              <Button icon={Download} loading={impor.isPending} onClick={() => impor.mutate()}>
                Impor ke Penyimpanan
              </Button>
            </div>
          </div>
        )
      }
    >
      {isLoading || !detail ? (
        <PageLoader label="Memuat dataset..." />
      ) : (
        <div className="space-y-4">
          {detail.description && (
            <p className="text-sm whitespace-pre-wrap text-[var(--text-secondary)]">
              {detail.description}
            </p>
          )}

          <dl className="divide-y divide-[var(--border-subtle)] rounded-lg border border-[var(--border-subtle)] px-4 py-1">
            <BarisInfo label="Jumlah baris" value={formatNumber(detail.rowCount)} />
            <BarisInfo label="Ukuran" value={formatBytes(detail.sizeBytes)} />
            <BarisInfo label="Lisensi" value={detail.license} />
            <BarisInfo label="Dipublikasikan oleh" value={detail.publishedBy?.name} />
            <BarisInfo label="Diperbarui" value={formatRelative(detail.dataUpdatedAt)} />
            <BarisInfo label="Sumber" value={detail.advanced?.sourceUrl} />
            <BarisInfo label="Metode pengumpulan" value={detail.advanced?.methodology} />
            <BarisInfo label="Frekuensi pembaruan" value={detail.advanced?.updateFrequency} />
            <BarisInfo label="Wilayah" value={detail.advanced?.region} />
            <BarisInfo
              label="Sudah dibersihkan"
              value={detail.advanced?.isCleaned ? 'Ya' : 'Data mentah'}
            />
            <BarisInfo label="Kontak" value={detail.advanced?.contactEmail} />
          </dl>

          {detail.advanced?.disclaimer && (
            <p className="rounded-lg bg-warning-50 p-3 text-xs text-[var(--text-secondary)] dark:bg-warning-500/10">
              {detail.advanced.disclaimer}
            </p>
          )}

          {detail.schemaFields?.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                Kolom
              </p>
              <div className="flex flex-wrap gap-1.5">
                {detail.schemaFields.map((f) => (
                  <Badge key={f.name} tone="neutral" size="sm">
                    {f.name}
                    {f.type ? ` · ${f.type}` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {contoh.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                Contoh baris
              </p>
              <div className="max-h-56 overflow-auto rounded-lg border border-[var(--border-subtle)]">
                <TableWrapper>
                  <Table>
                    <thead>
                      <Tr>
                        {headerContoh.map((h) => (
                          <Th key={h}>{h}</Th>
                        ))}
                      </Tr>
                    </thead>
                    <tbody>
                      {contoh.slice(0, 10).map((baris, i) => (
                        <Tr key={i}>
                          {headerContoh.map((h) => (
                            <Td key={h} className="max-w-52 truncate" title={String(baris[h] ?? '')}>
                              {String(baris[h] ?? '')}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </div>
            </div>
          )}
        </div>
      )}

      {inspeksi && (
        <DatasetPreviewDialog dataset={detail ?? dataset} onClose={() => setInspeksi(false)} />
      )}
    </Modal>
  );
};
